// One read, four outcomes, no invented data.
//
// `components/brain/lib/hooks.js` has `useBrainResource` and this is the same
// idea for Settings — deliberately re-stated rather than imported, because that
// hook is scoped to the Brain's own client and reaching across into another
// area's lib to borrow a hook is how two surfaces end up sharing a cache
// neither of them declared.
import { useCallback, useEffect, useState } from "react";

import { get } from "./api";

const LOADING = { status: "loading", data: null };

/**
 * Read `path` once on mount, and again whenever `reload()` is called.
 *
 * Returns { status, data, error, httpStatus, reload }, where status is one of
 * loading · ok · empty · denied · disabled · error. `empty` is derived here
 * rather than in each section, so "the endpoint answered and had nothing" is a
 * single decision instead of eight slightly different ones.
 *
 * `isEmpty` is optional; without it a section decides for itself.
 */
export default function useResource(path, { params, isEmpty, skip } = {}) {
  const [state, setState] = useState(skip ? { status: "skipped", data: null } : LOADING);
  const key = params ? JSON.stringify(params) : "";

  const load = useCallback(
    async (alive) => {
      if (skip) return;
      setState(LOADING);
      const res = await get(path, params);
      if (alive && !alive.current) return;
      if (res.status === "ok" && typeof isEmpty === "function" && isEmpty(res.data)) {
        setState({ ...res, status: "empty" });
        return;
      }
      setState(res);
    },
    // `params` is compared by value through `key` — an object literal in a
    // caller's render would otherwise re-fetch on every keystroke elsewhere in
    // the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, key, skip]
  );

  useEffect(() => {
    const alive = { current: true };
    load(alive);
    return () => {
      alive.current = false;
    };
  }, [load]);

  const reload = useCallback(() => load(null), [load]);

  return { ...state, reload };
}
